'use client';

import React, {
  createContext,
  use,
  useCallback,
  useMemo,
  ReactNode
} from 'react';
import { useSession } from 'next-auth/react';
import { useUserProfile } from '@/lib/services/profile/hooks';
import type { UserProfile, LawFirmProfile } from '@/lib/services/profile/types';

interface UserContextData {
  // User Data
  user: UserProfile | null;
  lawFirm: LawFirmProfile | null;

  // Permissions & Access
  tenantId: string | null;
  permissions: string[];

  // Loading States
  isLoading: boolean;
  error: string | null;

  // Actions
  refreshProfile: () => Promise<void>;

  // Utility Functions
  hasPermission: (permission: string) => boolean;
  canAccessMatter: (matterId: number) => boolean;
  canAccessLawFirm: (lawFirmId: number) => boolean;
}

/** @internal Exported for Storybook mock providers only */
export const UserContext = createContext<UserContextData | undefined>(
  undefined
);

interface UserProviderProps {
  children: ReactNode;
}

// Static role → permissions lookup. Hoisted out of the provider so it isn't
// re-allocated on every render and so React Compiler can preserve memoization
// of dependent values without bailing out.
const ROLE_PERMISSIONS: Record<string, string[]> = {
  admin: [
    'manage_users',
    'manage_firm',
    'delete_matters',
    'view_financials',
    'export_data',
    'manage_settings'
  ],
  user: [
    'view_matters',
    'create_matters',
    'edit_matters',
    'view_contacts',
    'manage_contacts'
  ]
};

/**
 * UserProvider - Provides comprehensive user and law firm data throughout the app
 *
 * This provider:
 * 1. Fetches and caches user profile data
 * 2. Provides law firm information for tenant isolation
 * 3. Manages permissions and access control
 * 4. Ensures data is fresh and synchronized
 */
export function UserProvider({ children }: UserProviderProps) {
  const { data: session, status } = useSession();
  const isAuthenticated = status === 'authenticated';

  const {
    profile,
    isLoading: profileLoading,
    error: profileError,
    fetchProfile
  } = useUserProfile({
    fetchOnMount: isAuthenticated,
    useCache: true
  });

  // Derive law firm data from profile (the basic info is enough today; a
  // production-grade fetch can replace this without changing call sites).
  const lawFirm = useMemo<LawFirmProfile | null>(() => {
    if (!profile?.lawFirm) return null;
    // createdAt / updatedAt are placeholders until a production-grade law-firm
    // fetch replaces this projection; keep them stable so the compiler can
    // preserve memoization downstream.
    return {
      id: profile.lawFirm.id.toString(),
      name: profile.lawFirm.name,
      email: profile.email,
      phone: profile.phoneNumber || '',
      createdAt: '',
      updatedAt: ''
    };
  }, [profile]);

  // Derive permissions from role. Memoized on the full profile so the
  // compiler's inferred dependency matches the manual one.
  const permissions = useMemo<string[]>(() => {
    if (!profile?.role) return [];
    return ROLE_PERMISSIONS[profile.role] || ROLE_PERMISSIONS.user;
  }, [profile]);

  const hasPermission = useCallback(
    (permission: string): boolean => permissions.includes(permission),
    [permissions]
  );

  const canAccessMatter = useCallback(
    (_matterId: number): boolean => isAuthenticated && !!profile?.lawFirm?.id,
    [isAuthenticated, profile?.lawFirm?.id]
  );

  const canAccessLawFirm = useCallback(
    (lawFirmId: number): boolean => profile?.lawFirm?.id === lawFirmId,
    [profile?.lawFirm?.id]
  );

  const refreshProfile = useCallback(async () => {
    await fetchProfile();
  }, [fetchProfile]);

  const tenantId =
    session?.tenantId || profile?.lawFirm?.id?.toString() || null;

  const contextValue = useMemo<UserContextData>(
    () => ({
      user: profile,
      lawFirm,
      tenantId,
      permissions: permissions || [],
      isLoading: profileLoading,
      error: profileError,
      refreshProfile,
      hasPermission,
      canAccessMatter,
      canAccessLawFirm
    }),
    [
      profile,
      lawFirm,
      tenantId,
      permissions,
      profileLoading,
      profileError,
      refreshProfile,
      hasPermission,
      canAccessMatter,
      canAccessLawFirm
    ]
  );

  return <UserContext value={contextValue}>{children}</UserContext>;
}

/**
 * Hook to access user context data
 *
 * @example
 * ```tsx
 * const { user, lawFirm, hasPermission } = useUser();
 *
 * if (!hasPermission('delete_matters')) {
 *   return <div>You don't have permission to delete matters</div>;
 * }
 * ```
 */
export function useUser() {
  const context = use(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

/**
 * Hook to ensure user has required permissions
 *
 * @example
 * ```tsx
 * const canDelete = usePermission('delete_matters');
 * ```
 */
export function usePermission(permission: string): boolean {
  const { hasPermission } = useUser();
  return hasPermission(permission);
}

/**
 * Hook to get tenant-specific data
 *
 * @example
 * ```tsx
 * const { tenantId, lawFirm } = useTenant();
 * ```
 */
export function useTenant() {
  const { tenantId, lawFirm } = useUser();
  return { tenantId, lawFirm };
}
